import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Table, Button, ButtonGroup, Pagination, Form, Row, Col, Card, Badge, Modal, Spinner, Alert, OverlayTrigger, Tooltip, Placeholder, Dropdown, Collapse } from 'react-bootstrap';
import { getAllTransactions, deleteTransaction, updateTransaction, getAllAccounts, getTickers } from '../services/api';
import { Link, useLocation } from 'react-router-dom';
import { formatDate, formatISODate } from '../utils/dateUtils';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';

// Custom styles for the component
const PAGE_SIZE_STORAGE_KEY = 'transactionsPageSize';
const DEFAULT_PAGE_SIZE = 10;

const SORT_COLUMN_STORAGE_KEY = 'transactions_sortColumn';
const SORT_DIRECTION_STORAGE_KEY = 'transactions_sortDirection';
const VALID_SORT_COLUMNS = new Set(['date', 'account', 'type', 'ticker', 'category', 'shares', 'price', 'total', 'gain', 'gain_pct', 'notes']);
const DEFAULT_SORT_COLUMN = 'date';
const DEFAULT_SORT_DIRECTION = 'desc';

const getDefaultTodayRange = () => {
  const today = formatISODate(new Date());
  return {
    startDate: today,
    endDate: today
  };
};

const getDefaultFilters = () => {
  // Default to showing today's transactions
  const today = formatISODate(new Date());

  return {
    symbol: '',
    accountId: '',
    type: '',
    startDate: today,
    endDate: today,
    timeframe: 'today'
  };
};

const customStyles = `
    .table-row-hover tbody tr {
        transition: background-color 0.15s ease-in-out;
    }
    .table-row-hover tbody tr:hover {
        background-color: rgba(0, 123, 255, 0.1) !important;
        cursor: pointer;
    }
    .transactions-empty-icon {
        color: #6c757d;
        opacity: 0.6;
    }
    .hover-bg-light:hover {
        background-color: #f8f9fa;
    }
    
    /* Enhanced card styling for maximum contrast and prominence */
    .transactions-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out;
        position: relative;
    }
    
    .transactions-card:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
        transform: translateY(-2px);
    }
    
    /* Light theme card enhancements - much more contrast */
    [data-theme="light"] .transactions-card {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .transactions-card:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme card enhancements - much more contrast */
    [data-theme="dark"] .transactions-card {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .transactions-card:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table styling for maximum contrast and prominence */
    .transactions-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px 8px 0 0 !important; /* Only top corners rounded */
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        transition: all 0.3s ease-in-out;
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }
    .transactions-table-container .table-responsive {
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
        overflow: unset !important;
    }
    .transactions-table-container .transactions-table {
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }
    
    /* Enhanced table header styling */
    .transactions-table-container .transactions-table thead th,
    .transactions-table-container .transactions-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }

    /* Remove the top border line that appears above the table header while keeping vertical borders */
    .transactions-table.table-bordered thead th,
    .transactions-table.table-bordered thead tr,
    .transactions-table.table-bordered thead,
    .transactions-table.table-bordered {
        border-top: 0 !important;
    }
    /* Also ensure the first header row cells do not render a top border */
    .transactions-table.table-bordered thead tr:first-child th {
        border-top: 0 !important;
    }
    
    .transactions-table-container .transactions-table thead th:hover,
    .transactions-table-container .transactions-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .transactions-table-container .transactions-table thead th,
    [data-theme="light"] .transactions-table-container .transactions-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .transactions-table-container .transactions-table thead th:hover,
    [data-theme="light"] .transactions-table-container .transactions-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .transactions-table-container .transactions-table thead th,
    [data-theme="dark"] .transactions-table-container .transactions-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .transactions-table-container .transactions-table thead th:hover,
    [data-theme="dark"] .transactions-table-container .transactions-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling with hover effects - more prominent and brighter */
    .transactions-table-container .transactions-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    
    .transactions-table-container .transactions-table tbody tr:hover {}
    [data-theme="light"] .transactions-table-container .transactions-table tbody tr:hover {}
    [data-theme="dark"] .transactions-table-container .transactions-table tbody tr:hover {}
    
    /* Enhanced table cell styling - more compact with hover effects */
    .transactions-table-container .transactions-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }

    .transactions-table-container .transactions-table tbody tr:not(.transactions-total-row):hover td {
        font-weight: 500 !important;
        transform: scale(1.02) !important;
    }

    .transactions-table-container .transactions-table tbody tr.transactions-total-row td {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
    }

    .transactions-table-container .transactions-table tbody tr.transactions-total-row:hover td {
        font-weight: 600 !important;
        transform: none !important;
    }
    
    [data-theme="light"] .transactions-table-container .transactions-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .transactions-table-container .transactions-table tbody tr:hover td {
        color: inherit !important;
    }
    
    [data-theme="dark"] .transactions-table-container .transactions-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .transactions-table-container .transactions-table tbody tr:hover td {
        color: inherit !important;
    }

    .transactions-gain-positive,
    .transactions-gain-negative,
    .transactions-gain-neutral {
        font-weight: 600 !important;
    }

    .transactions-gain-positive {
        color: rgba(var(--bs-success-rgb, 25, 135, 84), 1) !important;
    }

    .transactions-gain-negative {
        color: rgba(var(--bs-danger-rgb, 220, 53, 69), 1) !important;
    }

    .transactions-gain-neutral {
        color: inherit !important;
    }

    [data-theme="dark"] .transactions-gain-positive {
        color: #4ade80 !important;
    }

    [data-theme="dark"] .transactions-gain-negative {
        color: #f87171 !important;
    }
    
    /* Enhanced table styling - consistent with account details */
    .transactions-table-container .transactions-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .transactions-table-container .table-responsive {
        margin-bottom: 0 !important;
    }
    
    /* Less prominent delete button */
    .btn-delete-subtle {
        color: #6c757d !important;
        border-color: #6c757d !important;
        background-color: transparent !important;
        font-size: 0.875rem !important;
        padding: 0.25rem 0.5rem !important;
    }
    
    .btn-delete-subtle:hover {
        color: #dc3545 !important;
        border-color: #dc3545 !important;
        background-color: rgba(220, 53, 69, 0.1) !important;
    }
    
    [data-theme="dark"] .btn-delete-subtle {
        color: #9ca3af !important;
        border-color: #6b7280 !important;
    }
    
    [data-theme="dark"] .btn-delete-subtle:hover {
        color: #f87171 !important;
        border-color: #f87171 !important;
        background-color: rgba(248, 113, 113, 0.1) !important;
    }
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
`;

function Transactions({
  lockedFilters = {},
  pageTitle = 'Transactions',
  pageSubtitle = null
}) {
  const location = useLocation();
  
  // Pagination and data state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_PAGE_SIZE;
    }

    const storedValue = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const parsedValue = parseInt(storedValue, 10);

    if (Number.isFinite(parsedValue) && parsedValue > 0) {
      return parsedValue;
    }

    return DEFAULT_PAGE_SIZE;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);
  
  // Default column order
  const defaultColumnOrder = [
    'date',
    'account',
    'type',
    'ticker',
    'category',
    'shares',
    'price',
    'total',
    'gain',
    'gain_pct',
    'notes',
    'actions'
  ];
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem(`visibleColumns-transactions`);
    if (savedColumns) {
      const parsed = JSON.parse(savedColumns);
      // Migrate old 'purchase_price' column to 'price' and 'shares_amount' to 'shares'
      const migratedColumns = { ...parsed };
      if (parsed.purchase_price !== undefined) {
        migratedColumns.price = parsed.purchase_price;
        delete migratedColumns.purchase_price;
      }
      if (parsed.shares_amount !== undefined) {
        migratedColumns.shares = parsed.shares_amount;
        delete migratedColumns.shares_amount;
      }
      return {
        ...migratedColumns,
        // Ensure these columns are always present if not in localStorage
        date: migratedColumns.date !== undefined ? migratedColumns.date : true,
        account: migratedColumns.account !== undefined ? migratedColumns.account : true,
        type: migratedColumns.type !== undefined ? migratedColumns.type : true,
        ticker: migratedColumns.ticker !== undefined ? migratedColumns.ticker : true,
        category: migratedColumns.category !== undefined ? migratedColumns.category : true,
        gain: migratedColumns.gain !== undefined ? migratedColumns.gain : true,
        gain_pct: migratedColumns.gain_pct !== undefined ? migratedColumns.gain_pct : true,
        actions: migratedColumns.actions !== undefined ? migratedColumns.actions : true
      };
    }
    return {
      date: true,
      account: true,
      type: true,
      ticker: true,
      category: true,
      shares: true,
      price: true,
      total: true,
      gain: true,
      gain_pct: true,
      notes: true,
      actions: true
    };
  });
  
  // Column order state
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem(`columnOrder-transactions`);
    if (savedOrder) {
      const parsed = JSON.parse(savedOrder);
      // Migrate old 'purchase_price' column to 'price' and 'shares_amount' to 'shares'
      const migratedOrder = parsed.map(col => {
        if (col === 'purchase_price') return 'price';
        if (col === 'shares_amount') return 'shares';
        return col;
      });
      // Ensure new columns exist in order
      if (!migratedOrder.includes('gain')) {
        const totalIdx = migratedOrder.indexOf('total');
        const insertAt = totalIdx >= 0 ? totalIdx + 1 : migratedOrder.length;
        migratedOrder.splice(insertAt, 0, 'gain', 'gain_pct');
      }
      if (!migratedOrder.includes('category')) {
        const tickerIdx = migratedOrder.indexOf('ticker');
        const insertAt = tickerIdx >= 0 ? tickerIdx + 1 : migratedOrder.length;
        migratedOrder.splice(insertAt, 0, 'category');
      }
      return migratedOrder;
    }
    return defaultColumnOrder;
  });
  
  // Save column visibility settings when they change
  useEffect(() => {
    localStorage.setItem(`visibleColumns-transactions`, JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  
  // Save column order when it changes
  useEffect(() => {
    localStorage.setItem(`columnOrder-transactions`, JSON.stringify(columnOrder));
  }, [columnOrder]);
  
  // Parse URL query parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const symbolFromUrl = params.get('symbol');
    
    if (symbolFromUrl) {
      // Update filters with the symbol from URL
      setFilters(prev => ({
        ...prev,
        symbol: symbolFromUrl
      }));
      
      console.log(`Set symbol filter to ${symbolFromUrl} from URL query parameter`);
    }
  }, [location.search]);
  
  const mergedDefaultFilters = useMemo(() => ({
    ...getDefaultFilters(),
    ...lockedFilters
  }), [lockedFilters]);

  const filtersStorageKey = 'transactionsFilters';

  // Filter state
  const [filters, setFilters] = useState(() => {
    let storedFilters = {};
    try {
      const stored = localStorage.getItem(filtersStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          storedFilters = parsed;
        }
      }
    } catch {
      // Ignore malformed storage
    }
    return {
      ...getDefaultFilters(),
      ...storedFilters,
      ...lockedFilters
    };
  });

  // Only update filters when lockedFilters actually change (not on every render)
  useEffect(() => {
    // Check if we need to update by comparing values
    const needsUpdate = Object.keys(lockedFilters).some(key => {
      return filters[key] !== lockedFilters[key];
    });

    if (needsUpdate) {
      setFilters(prev => ({
        ...prev,
        ...lockedFilters
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedFilters]);

  const effectiveFilters = useMemo(() => ({
    ...filters,
    ...lockedFilters
  }), [filters, lockedFilters]);

  // Persist filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
    } catch {
      // Ignore storage errors
    }
  }, [filters]);

  const typeFilterLocked = Object.prototype.hasOwnProperty.call(lockedFilters, 'type');
  
  // Show/hide filters state
  const [showFilters, setShowFilters] = useState(true);
  
  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTransaction, setEditTransaction] = useState(null);
  const [editFormData, setEditFormData] = useState({
    ticker_id: '',
    shares: '',
    price_per_share: '',
    transaction_date: '',
    notes: '',
    is_stopout: false
  });
  
  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Reference data
  const [accounts, setAccounts] = useState([]);
  const [availableTickers, setAvailableTickers] = useState([]);
  // Sorting state
  const [sortColumn, setSortColumn] = useState(() => {
    const saved = localStorage.getItem(SORT_COLUMN_STORAGE_KEY);
    return saved && VALID_SORT_COLUMNS.has(saved) ? saved : DEFAULT_SORT_COLUMN;
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem(SORT_DIRECTION_STORAGE_KEY);
    return saved === 'asc' || saved === 'desc' ? saved : DEFAULT_SORT_DIRECTION;
  });

  // Helper functions for fetching data
  const fetchAccounts = async () => {
    try {
      const response = await getAllAccounts();
      setAccounts(response.data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchTickers = async () => {
    try {
      const response = await getTickers();
      setAvailableTickers(response.data);
    } catch (err) {
      console.error('Error fetching tickers:', err);
    }
  };

  const fetchTransactions = async () => {
    setTableLoading(true);
    setError('');

    console.log('Fetching transactions with filters:', effectiveFilters);

    try {
      const response = await getAllTransactions(currentPage, pageSize, effectiveFilters);
      console.log('API Response:', {
        transactionCount: response.data.transactions.length,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages
      });
      setTransactions(response.data.transactions);
      setTotalPages(response.data.totalPages);
      setTotalTransactions(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  // Load initial reference data once
  useEffect(() => {
    fetchAccounts();
    fetchTickers();
  }, []);

  // Fetch transactions when page or filters change with debouncing
  useEffect(() => {
    console.log('Filter/page change detected, scheduling fetch...', {
      currentPage,
      pageSize,
      symbol: filters.symbol,
      accountId: filters.accountId,
      type: filters.type,
      startDate: filters.startDate,
      endDate: filters.endDate
    });

    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timeoutId);
    };
    // Only depend on primitive filter values that trigger fetch, not fetchTransactions itself
    // fetchTransactions will use the latest effectiveFilters via closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters.symbol, filters.accountId, filters.type, filters.startDate, filters.endDate, filters.timeframe]);

  // Create a comparable value for sorting based on column
  const getAccountName = useCallback((accountId) => {
    const account = accounts.find(a => a.account_id === accountId);
    return account ? account.name : `Account ${accountId}`;
  }, [accounts]);

  const getSortValue = useCallback((tx, column) => {
    switch (column) {
      case 'date': {
        const d = tx.transaction_date ? new Date(tx.transaction_date) : null;
        return d ? d.getTime() : 0;
      }
      case 'account':
        return (getAccountName(tx.account_id) || '').toUpperCase();
      case 'type':
        return (tx.transaction_type || '').toUpperCase();
      case 'ticker':
        return (tx.ticker_symbol || '').toUpperCase();
      case 'category':
        return (tx.category || '').toUpperCase();
      case 'shares': {
        return Number(tx.shares) || 0;
      }
      case 'price':
        return tx.transaction_type === 'CASH' ? 0 : Number(tx.price_per_share) || 0;
      case 'total':
        return tx.transaction_type === 'CASH'
          ? Number(tx.shares) || 0
          : (Number(tx.shares) || 0) * (Number(tx.price_per_share) || 0);
      case 'gain':
        return tx.realized_gain_amount != null ? Number(tx.realized_gain_amount) : -Infinity; // Non-sell to bottom
      case 'gain_pct':
        return tx.realized_gain_percent != null ? Number(tx.realized_gain_percent) : -Infinity;
      case 'notes':
        return (tx.notes || '').toUpperCase();
      default:
        return '';
    }
  }, [getAccountName]);

  // Sort when inputs change
  const sortedTransactions = useMemo(() => {
    const list = [...transactions];
    list.sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDirection === 'asc' ? av - bv : bv - av;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDirection === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return list;
  }, [transactions, sortColumn, sortDirection, getSortValue]);

  const handleHeaderClick = (column) => {
    if (column === 'actions') return;
    const newDir = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDir);
    localStorage.setItem(SORT_COLUMN_STORAGE_KEY, column);
    localStorage.setItem(SORT_DIRECTION_STORAGE_KEY, newDir);
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };
  
  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (Object.prototype.hasOwnProperty.call(lockedFilters, name)) {
      return;
    }
    
    // For symbol filter, trim whitespace and ensure consistent case
    const processedValue = name === 'symbol' ? value.trim() : value;
    
    setFilters(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  
  const resetFilters = () => {
    setFilters({ ...mergedDefaultFilters });
    setCurrentPage(1);
  };
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);

    if (Number.isFinite(newSize) && newSize > 0) {
      setPageSize(newSize);
    } else {
      setPageSize(DEFAULT_PAGE_SIZE);
    }

    setCurrentPage(1); // Reset to first page
  };
  
  // Delete handlers
  const openDeleteModal = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };
  
  const closeDeleteModal = () => {
    setTransactionToDelete(null);
    setShowDeleteModal(false);
  };
  
  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    setDeleteLoading(true);
    
    try {
      await deleteTransaction(transactionToDelete.transaction_id);
      fetchTransactions(); // Refresh the list
      setShowDeleteModal(false);
      
      // Show success message
      setError({ type: 'success', message: 'Transaction deleted successfully' });
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction. Please try again.');
    } finally {
      setDeleteLoading(false);
      setTransactionToDelete(null);
    }
  };
  
  // Edit handlers
  const openEditModal = (transaction) => {
    setEditTransaction(transaction);

    // Format the date properly to YYYY-MM-DD for the input field
    let formattedDate = '';
    try {
      // Use the raw_date field if it exists (new API response format)
      if (transaction.raw_date) {
        formattedDate = transaction.raw_date.split('T')[0]; // Extract YYYY-MM-DD part
      } else {
        // Handle different date formats that might come from the API
        if (transaction.transaction_date) {
          // Fix timezone issue by parsing date parts directly
          const dateParts = transaction.transaction_date.split('T')[0].split('-');
          if (dateParts.length === 3) {
            formattedDate = dateParts.join('-'); // Keep in YYYY-MM-DD format
          } else {
            // Fallback to previous method
            const date = new Date(transaction.transaction_date);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toLocaleDateString('en-CA');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      // Fallback to empty string if date parsing fails
      formattedDate = '';
    }

    setEditFormData({
      ticker_id: transaction.ticker_id,
      shares: transaction.shares,
      price_per_share: transaction.price_per_share,
      transaction_date: formattedDate,
      notes: transaction.notes || '',
      is_stopout: Boolean(transaction.is_stopout && transaction.transaction_type === 'SELL')
    });
    
    setShowEditModal(true);
  };
  
  const closeEditModal = () => {
    setEditTransaction(null);
    setShowEditModal(false);
  };
  
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleUpdateTransaction = async () => {
    if (!editTransaction) return;
    
    try {
      await updateTransaction(editTransaction.transaction_id, editFormData);
      fetchTransactions(); // Refresh the list
      setShowEditModal(false);
      
      // Show success message
      setError({ type: 'success', message: 'Transaction updated successfully' });
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError('Failed to update transaction. Please try again.');
    }
  };
  
  // formatDate function is now imported from dateUtils
  
  const totalLabelColumn = useMemo(() => {
    const firstDataColumn = columnOrder.find(column => visibleColumns[column] && column !== 'actions');
    return firstDataColumn || columnOrder.find(column => visibleColumns[column]) || null;
  }, [columnOrder, visibleColumns]);

  const accountsTotalValue = useMemo(() => {
    return accounts.reduce((sum, account) => {
      if (!account) return sum;
      const rawBalance = account.balance != null ? account.balance : account.total_value;
      const numericBalance = Number(rawBalance);
      return Number.isFinite(numericBalance) ? sum + numericBalance : sum;
    }, 0);
  }, [accounts]);

  const {
    totalGainAmount,
    totalGainPercent,
    hasGainAmount,
    hasGainPercent,
    totalBuyAmount,
    hasBuyAmount
  } = useMemo(() => {
    let amount = 0;
    let hasAmount = false;

    sortedTransactions.forEach(tx => {
      if (tx.transaction_type !== 'SELL') return;

      const amountValue = tx.realized_gain_amount != null ? Number(tx.realized_gain_amount) : null;

      if (amountValue != null && !Number.isNaN(amountValue)) {
        amount += amountValue;
        hasAmount = true;
      }
    });

    const percent = accountsTotalValue > 0 ? (amount / accountsTotalValue) * 100 : 0;
    const hasPercent = hasAmount && accountsTotalValue > 0;

    // Calculate total for BUY transactions
    let buyAmount = 0;
    let hasBuy = false;

    sortedTransactions.forEach(tx => {
      if (tx.transaction_type === 'BUY') {
        const totalValue = tx.transaction_type === 'CASH'
          ? Number(tx.shares)
          : Number(tx.shares) * Number(tx.price_per_share);

        if (!Number.isNaN(totalValue)) {
          buyAmount += totalValue;
          hasBuy = true;
        }
      }
    });

    return {
      totalGainAmount: amount,
      totalGainPercent: percent,
      hasGainAmount: hasAmount,
      hasGainPercent: hasPercent,
      totalBuyAmount: buyAmount,
      hasBuyAmount: hasBuy
    };
  }, [sortedTransactions, accountsTotalValue]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };
  
  // Render pagination controls
  const renderPagination = () => {
    const items = [];
    const maxItems = 7; // Maximum number of page items to show
    
    // Add first page
    items.push(
      <Pagination.Item 
        key={1} 
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Calculate start and end pages
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust for edge cases
    if (currentPage <= 3) {
      endPage = Math.min(totalPages - 1, maxItems - 2);
    } else if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - maxItems + 2);
    }
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    
    // Add intermediate pages
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }
    
    // Add last page if there are more than 1 page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={totalPages} 
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select 
            size="sm" 
            value={pageSize} 
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
          >
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
          <span className="ms-3">
            {totalTransactions > 0 ? (
              <>
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalTransactions)} of {totalTransactions} transactions
              </>
            ) : (
              'No transactions found'
            )}
          </span>
        </div>
        
        <Pagination size="sm">
          <Pagination.First 
            onClick={() => handlePageChange(1)} 
            disabled={currentPage === 1}
          />
          <Pagination.Prev 
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
            disabled={currentPage === 1}
          />
          
          {items}
          
          <Pagination.Next 
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
            disabled={currentPage === totalPages}
          />
          <Pagination.Last 
            onClick={() => handlePageChange(totalPages)} 
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };
  
  // Render pagination placeholder when loading
  const renderPaginationPlaceholder = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <Placeholder as="span" animation="glow" className="me-2">
            <Placeholder xs={6} />
          </Placeholder>
          <Placeholder as={Form.Select} animation="glow" style={{ width: '80px' }} />
          <Placeholder as="span" animation="glow" className="ms-3">
            <Placeholder xs={8} />
          </Placeholder>
        </div>
        <Placeholder.Button variant="primary" xs={5} />
      </div>
    );
  };
  
  // Handle timeframe selection
  const handleTimeframeChange = (e) => {
    const { value } = e.target;
    
    // Calculate start and end dates based on selected timeframe
    const endDate = new Date();
    let startDate = new Date();
    
    switch(value) {
      case 'today':
        // Set start date to the beginning of the current day
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0);
        break;
      case '2days':
        startDate.setDate(endDate.getDate() - 2);
        break;
      case '3days':
        startDate.setDate(endDate.getDate() - 3);
        break;
      case '1week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '2weeks':
        startDate.setDate(endDate.getDate() - 14);
        break;
      case '1month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case '2months':
        startDate.setMonth(endDate.getMonth() - 2);
        break;
      case '6months':
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case 'custom':
        // For custom, don't modify dates
        setFilters(prev => ({
          ...prev,
          timeframe: value
        }));
        return;
      default:
        startDate.setDate(endDate.getDate() - 7); // Default to 1 week
    }
    
    // Format dates as YYYY-MM-DD
    setFilters(prev => ({
      ...prev,
      timeframe: value,
      startDate: formatISODate(startDate),
      endDate: formatISODate(endDate)
    }));
    
    // Reset to first page
    setCurrentPage(1);
  };

  // Keep "Today" timeframe aligned with the current day
  useEffect(() => {
    if (filters.timeframe === 'today') {
      const { startDate: todayStart, endDate: todayEnd } = getDefaultTodayRange();
      if (filters.startDate !== todayStart || filters.endDate !== todayEnd) {
        setFilters(prev => ({
          ...prev,
          startDate: todayStart,
          endDate: todayEnd
        }));
      }
    }
  }, [filters.timeframe, filters.startDate, filters.endDate]);

  const typeFilterValue = typeFilterLocked ? lockedFilters.type : filters.type;
  
  return (
    <Container className="py-4">
      <style>{customStyles}</style>
      {/* Filter and Columns buttons */}
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-primary"
          onClick={() => setShowFilters(!showFilters)}
          size="sm"
          className="text-nowrap me-2"
        >
          <i className={`bi bi-funnel${showFilters ? '-fill' : ''} me-1`}></i>
          Filters
        </Button>
        
        {visibleColumns && (
          <ColumnVisibilityDropdown
            visibleColumns={visibleColumns}
            columns={columnOrder}
            disabledColumns={['date', 'actions']} // Essential columns can't be disabled
            onToggleColumn={(column, newValue) => {
              setVisibleColumns(prev => ({
                ...prev,
                [column]: newValue
              }));
            }}
            idPrefix="header-column-toggle"
            disabled={loading}
          >
            <Dropdown.Divider />
            <div className="text-muted small">
              <div>• Essential columns cannot be hidden</div>
            </div>
            <Dropdown.Divider />
            <Button
              variant="outline-secondary"
              size="sm"
              className="w-100"
              onClick={() => {
                // Reset to defaults
                setColumnOrder(defaultColumnOrder);
                setVisibleColumns({
                  date: true,
                  account: true,
                  type: true,
                  ticker: true,
                  category: true,
                  shares: true,
                  price: true,
                  total: true,
                  gain: true,
                  gain_pct: true,
                  notes: true,
                  actions: true
                });
                // Clear localStorage
                localStorage.removeItem(`columnOrder-transactions`);
                localStorage.removeItem(`visibleColumns-transactions`);
              }}
            >
              Reset Columns
            </Button>
          </ColumnVisibilityDropdown>
        )}
      </div>

      {/* Filter Card */}
      <Collapse in={showFilters}>
        <Card className="mb-4 transactions-card">
          <Card.Body className="p-2">
            <Form>
              <Row className="g-2">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Symbol</Form.Label>
                    {loading ? (
                      <Placeholder as={Form.Control} animation="glow" size="sm" />
                    ) : (
                      <Form.Control 
                        type="text"
                        name="symbol"
                        value={filters.symbol}
                        onChange={handleFilterChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                        placeholder="Enter ticker"
                        size="sm"
                      />
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Account</Form.Label>
                    {loading ? (
                      <Placeholder as={Form.Select} animation="glow" size="sm" />
                    ) : (
                      <Form.Select
                        name="accountId"
                        value={filters.accountId}
                        onChange={handleFilterChange}
                        size="sm"
                      >
                        <option value="">All Accounts</option>
                        {accounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Timeframe</Form.Label>
                    {loading ? (
                      <Placeholder as={Form.Select} animation="glow" size="sm" />
                    ) : (
                      <Form.Select
                        name="timeframe"
                        value={filters.timeframe}
                        onChange={handleTimeframeChange}
                        size="sm"
                      >
                        <option value="today">Today</option>
                        <option value="2days">2 Days</option>
                        <option value="3days">3 Days</option>
                        <option value="1week">1 Week</option>
                        <option value="2weeks">2 Weeks</option>
                        <option value="1month">1 Month</option>
                        <option value="2months">2 Months</option>
                        <option value="6months">6 Months</option>
                        <option value="custom">Custom</option>
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={2} className={filters.timeframe === 'custom' ? '' : 'd-none'}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Start Date</Form.Label>
                    {loading ? (
                      <Placeholder as={Form.Control} animation="glow" size="sm" />
                    ) : (
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        size="sm"
                      />
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={2} className={filters.timeframe === 'custom' ? '' : 'd-none'}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">End Date</Form.Label>
                    {loading ? (
                      <Placeholder as={Form.Control} animation="glow" size="sm" />
                    ) : (
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        size="sm"
                      />
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Type</Form.Label>
                    <ButtonGroup size="sm" className="w-100">
                      <Button
                        variant={typeFilterValue === '' ? 'primary' : 'outline-primary'}
                        disabled={loading || typeFilterLocked}
                        onClick={() => {
                          if (!typeFilterLocked) {
                            setFilters(prev => ({ ...prev, type: '' }));
                          }
                        }}
                      >
                        All
                      </Button>
                      <Button
                        variant={typeFilterValue === 'BUY' ? 'primary' : 'outline-primary'}
                        disabled={loading || typeFilterLocked}
                        onClick={() => {
                          if (!typeFilterLocked) {
                            setFilters(prev => ({ ...prev, type: 'BUY' }));
                          }
                        }}
                      >
                        Buy
                      </Button>
                      <Button
                        variant={typeFilterValue === 'SELL' ? 'primary' : 'outline-primary'}
                        disabled={loading || typeFilterLocked}
                        onClick={() => {
                          if (!typeFilterLocked) {
                            setFilters(prev => ({ ...prev, type: 'SELL' }));
                          }
                        }}
                      >
                        Sell
                      </Button>
                      <Button
                        variant={typeFilterValue === 'CASH' ? 'primary' : 'outline-primary'}
                        disabled={loading || typeFilterLocked}
                        onClick={() => {
                          if (!typeFilterLocked) {
                            setFilters(prev => ({ ...prev, type: 'CASH' }));
                          }
                        }}
                      >
                        Cash
                      </Button>
                    </ButtonGroup>
                  </Form.Group>
                </Col>
                <Col md="auto" className="d-flex align-items-end ms-auto">
                  <Button 
                    variant="outline-secondary" 
                    onClick={resetFilters}
                    size="sm"
                    className="text-nowrap"
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Collapse>
      
      {/* Error/success message */}
      {error && typeof error === 'object' && error.type === 'success' ? (
        <Alert variant="success" className="mb-3">
          {error.message}
        </Alert>
      ) : error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {/* Transactions Table */}
      <div className="transactions-table-container">
        <div className="table-responsive">
          {tableLoading ? (
            <Table responsive className="align-middle transactions-table" style={{ tableLayout: 'fixed', width: '100%', overflowX: 'auto' }} bordered>
          <thead>
            <tr>
              {columnOrder.map(column => {
                if (!visibleColumns[column]) return null;
                return (
                  <th key={column} className="py-2" onClick={() => handleHeaderClick(column)} style={
                    column === 'actions' 
                      ? { textAlign: 'center', width: '120px', cursor: 'pointer' }
                      : column === 'date'
                        ? { minWidth: '120px', width: '120px', cursor: 'pointer' }
                        : column === 'account'
                        ? { width: '100px', cursor: 'pointer' }
                        : column === 'type'
                        ? { width: '80px', cursor: 'pointer' }
                        : column === 'ticker'
                        ? { width: '80px', cursor: 'pointer' }
                        : column === 'category'
                        ? { width: '100px', cursor: 'pointer' }
                        : column === 'shares'
                        ? { width: '80px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'price'
                        ? { width: '90px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'total'
                        ? { width: '120px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'gain'
                        ? { width: '108px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'gain_pct'
                        ? { width: '80px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'notes'
                        ? { width: 'calc(100% - 38px)', cursor: 'pointer' }
                        : { cursor: 'pointer' }
                  }>
                    {column === 'gain' ? 'Gain$' : 
                     column === 'gain_pct' ? 'Gain%' : 
                     column.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}{renderSortIndicator(column)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Array(pageSize).fill(0).map((_, index) => (
              <tr key={index} className="border-bottom">
                {columnOrder.map(column => {
                  if (!visibleColumns[column]) return null;
                  if (column === 'actions') {
                    return (
                      <td key={column} className="py-1" style={{ textAlign: 'center', width: '120px' }}>
                        <Placeholder animation="glow" className="d-inline-block">
                          <Placeholder.Button xs={4} variant="primary" className="me-1" />
                          <Placeholder.Button xs={4} variant="danger" />
                        </Placeholder>
                      </td>
                    );
                  }
                  return (
                    <td key={column} className="py-1">
                      <Placeholder animation="glow">
                        <Placeholder xs={column === 'ticker' ? 5 : column === 'shares' ? 4 : column === 'account' ? 8 : 6} />
                      </Placeholder>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
            <Table 
              responsive 
              hover 
              className="align-middle transactions-table" 
              style={{ 
                tableLayout: 'fixed',
                width: '100%',
                overflowX: 'auto'
              }}
              bordered
            >
          <thead>
            <tr>
              {columnOrder.map(column => {
                if (!visibleColumns[column]) return null;
                return (
                  <th key={column} className="py-1" onClick={() => handleHeaderClick(column)} style={
                    column === 'actions' 
                      ? { textAlign: 'center', width: '120px', cursor: 'pointer' }
                      : column === 'date'
                        ? { minWidth: '120px', width: '120px', cursor: 'pointer' }
                        : column === 'account'
                        ? { width: '100px', cursor: 'pointer' }
                        : column === 'type'
                        ? { width: '80px', cursor: 'pointer' }
                        : column === 'ticker'
                        ? { width: '80px', cursor: 'pointer' }
                        : column === 'category'
                        ? { width: '100px', cursor: 'pointer' }
                        : column === 'shares'
                        ? { width: '80px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'price'
                        ? { width: '90px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'total'
                        ? { width: '120px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'gain'
                        ? { width: '108px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'gain_pct'
                        ? { width: '80px', textAlign: 'right', cursor: 'pointer' }
                        : column === 'notes'
                        ? { width: 'calc(100% - 38px)', cursor: 'pointer' }
                        : { cursor: 'pointer' }
                  }>
                    {column === 'gain' ? 'Gain$' : 
                     column === 'gain_pct' ? 'Gain%' : 
                     column.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}{renderSortIndicator(column)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="text-center py-4">
                  No transactions found. Try adjusting your filters.
                </td>
              </tr>
            ) : (
              <>
                {sortedTransactions.map(transaction => (
                  <tr
                    key={transaction.transaction_id}
                    className="border-bottom"
                  >
                    {columnOrder.map(column => {
                      if (!visibleColumns[column]) return null;

                      switch(column) {
                      case 'date':
                        return <td key={column} className="py-1" style={{ width: '120px' }}>{formatDate(transaction.transaction_date)}</td>;
                      case 'account':
                        return (
                          <td key={column} className="py-1" style={{ width: '100px' }}>
                            <Link to={`/account/${transaction.account_id}`}>
                              {getAccountName(transaction.account_id)}
                            </Link>
                          </td>
                        );
                      case 'type':
                        return (
                          <td key={column} className="py-1" style={{ width: '80px' }}>
                            <div className="d-flex flex-column align-items-start gap-1">
                              <Badge bg={
                                transaction.transaction_type === 'BUY' ? 'success' :
                                transaction.transaction_type === 'SELL' ? 'danger' : 'info'
                              }>
                                {transaction.transaction_type}
                              </Badge>
                              {transaction.transaction_type === 'SELL' && transaction.is_stopout && (
                                <Badge bg="warning" text="dark">
                                  Stopout
                                </Badge>
                              )}
                            </div>
                          </td>
                        );
                      case 'ticker':
                        return (
                          <td key={column} className="py-1" style={{ width: '80px' }}>
                            {transaction.transaction_type === 'CASH' ? (
                              '—'
                            ) : (
                              <span style={{ color: '#007bff', fontWeight: 'bold' }}>{transaction.ticker_symbol}</span>
                            )}
                          </td>
                        );
                      case 'category':
                        return (
                          <td key={column} className="py-1" style={{ width: '100px' }}>
                            {transaction.transaction_type === 'CASH' ? '—' : (transaction.category || '—')}
                          </td>
                        );
                      case 'shares':
                        return (
                          <td key={column} className="py-1 text-end" style={{ width: '80px' }}>
                            {transaction.transaction_type === 'CASH' ? (
                              formatCurrency(transaction.shares)
                            ) : (
                              Number.isInteger(parseFloat(transaction.shares)) 
                                ? parseInt(transaction.shares) 
                                : parseFloat(transaction.shares).toFixed(2)
                            )}
                          </td>
                        );
                      case 'price':
                        return (
                          <td key={column} className="py-1 text-end" style={{ width: '90px' }}>
                            {transaction.transaction_type === 'CASH' ? (
                              '—'
                            ) : (
                              formatCurrency(transaction.price_per_share)
                            )}
                          </td>
                        );
                      case 'total':
                        return (
                          <td key={column} className="py-1 text-end" style={{ width: '120px' }}>
                            {formatCurrency(
                              transaction.transaction_type === 'CASH' 
                                ? transaction.shares
                                : transaction.shares * transaction.price_per_share
                            )}
                          </td>
                        );
                      case 'gain': {
                        if (transaction.transaction_type !== 'SELL') {
                          return <td key={column} className="py-1 text-end text-muted" style={{ width: '140px' }}>—</td>;
                        }

                        const gainValueRaw =
                          transaction.realized_gain_amount != null
                            ? Number(transaction.realized_gain_amount)
                            : null;
                        const gainValue =
                          gainValueRaw != null && !Number.isNaN(Number(gainValueRaw))
                            ? Number(gainValueRaw)
                            : null;

                        if (gainValue == null) {
                          return <td key={column} className="py-1 text-end text-muted" style={{ width: '140px' }}>—</td>;
                        }

                        const gainClasses = ['py-1', 'text-end'];
                        if (gainValue > 0) {
                          gainClasses.push('transactions-gain-positive');
                        } else if (gainValue < 0) {
                          gainClasses.push('transactions-gain-negative');
                        } else {
                          gainClasses.push('transactions-gain-neutral');
                        }

                        return (
                          <td key={column} className={gainClasses.join(' ')} style={{ width: '140px' }}>
                            {formatCurrency(gainValue)}
                          </td>
                        );
                      }
                      case 'gain_pct': {
                        if (transaction.transaction_type !== 'SELL') {
                          return <td key={column} className="py-1 text-end text-muted" style={{ width: '80px' }}>—</td>;
                        }
                        const gainPercentRaw =
                          transaction.realized_gain_percent != null
                            ? Number(transaction.realized_gain_percent)
                            : null;
                        const gainPercent =
                          gainPercentRaw != null && !Number.isNaN(Number(gainPercentRaw))
                            ? Number(gainPercentRaw)
                            : null;

                        if (gainPercent == null) {
                          return <td key={column} className="py-1 text-end text-muted" style={{ width: '80px' }}>—</td>;
                        }

                        const gainPctClasses = ['py-1', 'text-end'];
                        if (gainPercent > 0) {
                          gainPctClasses.push('transactions-gain-positive');
                        } else if (gainPercent < 0) {
                          gainPctClasses.push('transactions-gain-negative');
                        } else {
                          gainPctClasses.push('transactions-gain-neutral');
                        }

                        return (
                          <td key={column} className={gainPctClasses.join(' ')} style={{ width: '80px' }}>
                            {`${gainPercent.toFixed(2)}%`}
                          </td>
                        );
                      }
                      case 'notes':
                        return (
                          <td key={column} className="py-1" style={{ width: '260px' }}>
                            {transaction.notes ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-notes-${transaction.transaction_id}`} style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                                    {transaction.notes}
                                  </Tooltip>
                                }
                              >
                                <span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                                  {transaction.notes}
                                </span>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        );
                      case 'actions':
                        return (
                          <td key={column} className="py-1" style={{ textAlign: 'center', width: '120px' }}>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              className="me-1 py-0 px-1"
                              style={{ lineHeight: '1.2' }}
                              onClick={() => openEditModal(transaction)}
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="outline-secondary"
                              size="sm"
                              className="py-0 px-1 btn-delete-subtle"
                              style={{ lineHeight: '1.2' }}
                              onClick={() => openDeleteModal(transaction)}
                            >
                              <i className="bi bi-trash3" style={{ fontSize: '0.75rem' }}></i>
                            </Button>
                          </td>
                        );
                      default:
                        return null;
                    }
                  })}
                </tr>
              ))}
                {sortedTransactions.length > 0 && (
                  <tr className="transactions-total-row" key="transactions-total-row">
                    {columnOrder.map(column => {
                      if (!visibleColumns[column]) return null;

                      if (column === 'gain') {
                        const gainClasses = ['py-1', 'text-end'];
                        if (hasGainAmount) {
                          if (totalGainAmount > 0) {
                            gainClasses.push('transactions-gain-positive');
                          } else if (totalGainAmount < 0) {
                            gainClasses.push('transactions-gain-negative');
                          } else {
                            gainClasses.push('transactions-gain-neutral');
                          }
                        } else {
                          gainClasses.push('text-muted');
                        }

                        return (
                          <td key={column} className={gainClasses.join(' ')} style={{ width: '140px' }}>
                            {hasGainAmount ? formatCurrency(totalGainAmount) : '—'}
                          </td>
                        );
                      }

                      if (column === 'gain_pct') {
                        const gainPctClasses = ['py-1', 'text-end'];
                        if (hasGainPercent) {
                          if (totalGainPercent > 0) {
                            gainPctClasses.push('transactions-gain-positive');
                          } else if (totalGainPercent < 0) {
                            gainPctClasses.push('transactions-gain-negative');
                          } else {
                            gainPctClasses.push('transactions-gain-neutral');
                          }
                        } else {
                          gainPctClasses.push('text-muted');
                        }

                        const percentDisplay = hasGainPercent ? `${totalGainPercent.toFixed(2)}%` : '—';
                        return (
                          <td key={column} className={gainPctClasses.join(' ')} style={{ width: '80px' }}>
                            {percentDisplay}
                          </td>
                        );
                      }

                      if (column === 'total') {
                        // Show total for BUY transactions when filter type is BUY
                        if (filters.type === 'BUY' && hasBuyAmount) {
                          return (
                            <td key={column} className="py-1 text-end fw-bold" style={{ width: '120px' }}>
                              {formatCurrency(totalBuyAmount)}
                            </td>
                          );
                        }
                        // Otherwise show empty cell
                        return (
                          <td key={column} className="py-1 text-end text-muted" style={{ width: '120px' }}>
                            {''}
                          </td>
                        );
                      }

                      if (column === 'actions') {
                        return (
                          <td key={column} className="py-1" style={{ textAlign: 'center', width: '120px' }}></td>
                        );
                      }

                      if (column === totalLabelColumn) {
                        const baseStyle =
                          column === 'date'
                            ? { minWidth: '120px', width: '120px' }
                            : column === 'account'
                              ? { width: '100px' }
                              : column === 'type'
                                ? { width: '80px' }
                                : column === 'ticker'
                                  ? { width: '80px' }
                                  : column === 'category'
                                    ? { width: '100px' }
                                  : column === 'notes'
                                    ? { width: '260px' }
                                    : {};
                        return (
                          <td key={column} className="py-1" style={baseStyle}>
                            Total
                          </td>
                        );
                      }

                      const isNumericColumn = ['shares', 'price', 'total'].includes(column);
                      const baseStyle =
                        column === 'date'
                          ? { minWidth: '120px', width: '120px' }
                          : column === 'account'
                            ? { width: '100px' }
                            : column === 'type'
                              ? { width: '80px' }
                              : column === 'ticker'
                                ? { width: '80px' }
                                : column === 'category'
                                  ? { width: '100px' }
                                : column === 'shares'
                                  ? { width: '80px' }
                                  : column === 'price'
                                    ? { width: '90px' }
                                    : column === 'total'
                                      ? { width: '120px' }
                                      : column === 'notes'
                                        ? { width: '260px' }
                                        : {};

                      return (
                        <td
                          key={column}
                          className={`py-1 ${isNumericColumn ? 'text-end ' : ''}text-muted`}
                          style={baseStyle}
                        >
                          {''}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </>
            )}
          </tbody>
            </Table>
          )}
        </div>
      </div>
      
      {/* Bottom pagination - show placeholder when loading */}
      {loading ? renderPaginationPlaceholder() : renderPagination()}
      
      {/* Edit Transaction Modal */}
      <Modal show={showEditModal} onHide={closeEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Account information (not editable) */}
            {editTransaction && (
              <Form.Group className="mb-3">
                <Form.Label>Account</Form.Label>
                <Form.Control
                  type="text"
                  value={getAccountName(editTransaction.account_id)}
                  disabled
                  className="form-control-dark-disabled"
                />
              </Form.Group>
            )}
            
            {editTransaction?.transaction_type !== 'CASH' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Ticker</Form.Label>
                  <Form.Select 
                    name="ticker_id" 
                    value={editFormData.ticker_id || ''}
                    onChange={handleEditFormChange}
                    disabled={true} // Can't change ticker
                    className="form-control-dark-disabled"
                  >
                    <option value="">Select Ticker</option>
                    {availableTickers.map(ticker => (
                      <option key={ticker.ticker_id} value={ticker.ticker_id}>
                        {ticker.symbol} - {ticker.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Shares</Form.Label>
                  <Form.Control 
                    type="number"
                    name="shares"
                    value={editFormData.shares}
                    onChange={handleEditFormChange}
                    step="0.000001"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Price Per Share</Form.Label>
                  <Form.Control 
                    type="number"
                    name="price_per_share"
                    value={editFormData.price_per_share}
                    onChange={handleEditFormChange}
                    step="0.01"
                  />
                </Form.Group>
              </>
            )}
            
            {editTransaction?.transaction_type === 'CASH' && (
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control 
                  type="number"
                  name="shares"
                  value={editFormData.shares}
                  onChange={handleEditFormChange}
                  step="0.01"
                />
              </Form.Group>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Transaction Date</Form.Label>
              <Form.Control 
                type="date"
                name="transaction_date"
                value={editFormData.transaction_date}
                onChange={handleEditFormChange}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control 
                as="textarea"
                rows={3}
                name="notes"
                value={editFormData.notes}
                onChange={handleEditFormChange}
              />
            </Form.Group>

            {editTransaction?.transaction_type === 'SELL' && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="edit-stopout-flag"
                  name="is_stopout"
                  label="Stop loss hit (mark as stopout)"
                  checked={Boolean(editFormData.is_stopout)}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateTransaction}>
            Update Transaction
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {transactionToDelete && (
            <div>
              <p>Are you sure you want to delete this transaction?</p>
              <ul className="list-unstyled">
                <li><strong>Date:</strong> {formatDate(transactionToDelete.transaction_date)}</li>
                <li><strong>Account:</strong> {getAccountName(transactionToDelete.account_id)}</li>
                <li>
                  <strong>Type:</strong> {transactionToDelete.transaction_type}
                </li>
                {transactionToDelete.transaction_type !== 'CASH' && (
                  <>
                    <li><strong>Ticker:</strong> {transactionToDelete.ticker_symbol}</li>
                    <li><strong>Shares:</strong> {transactionToDelete.shares}</li>
                    <li><strong>Purchase Price:</strong> {formatCurrency(transactionToDelete.price_per_share)}</li>
                  </>
                )}
                {transactionToDelete.transaction_type === 'CASH' && (
                  <li><strong>Amount:</strong> {formatCurrency(transactionToDelete.shares)}</li>
                )}
                <li>
                  <strong>Total:</strong> {formatCurrency(
                    transactionToDelete.transaction_type === 'CASH' 
                      ? transactionToDelete.shares
                      : transactionToDelete.shares * transactionToDelete.price_per_share
                  )}
                </li>
              </ul>
              <Alert variant="warning">
                <strong>Warning:</strong> Deleting this transaction will update your account balances and may affect your historical performance data.
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteTransaction}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              'Delete Transaction'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Transactions; 
